import { cn } from '@nexo/ui';
import { Hash, ListTodo, MousePointer2, Phone, SendHorizontal } from 'lucide-react';
import { type SubmitEvent, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PriorityIcon, StatusIcon } from '../tasks/TaskIcons';

/**
 * «A normal day in Nexo»: the landing's product preview, alive. A scripted conversation plays in a
 * loop while it's on screen (people typing, reactions, a message becoming a task that lands in
 * «My tasks», a task getting done, a call starting) and visitors can write in the composer.
 * Texts are Spanish keys translated at render, like the rest of the app.
 */

type Status = 'todo' | 'in_progress' | 'done';
interface Message {
  id: number;
  who: Person;
  text: string;
  reactions?: string[];
  task?: boolean;
  mine?: boolean;
}
interface Task {
  id: string;
  title: string;
  status: Status;
  group: 'overdue' | 'today' | 'week';
  urgent?: boolean;
  fresh?: boolean;
}
interface State {
  messages: Message[];
  typing: Person | null;
  hint: boolean;
  tasks: Task[];
  call: number;
}

const people = {
  lucia: { name: 'Lucía', tone: 0 },
  marcos: { name: 'Marcos', tone: 1 },
  irene: { name: 'Irene', tone: 3 },
  you: { name: 'Tú', tone: 2 },
} as const;
type Person = keyof typeof people;

const initial: State = {
  messages: [{ id: 1, who: 'irene', text: 'Buenos días 👋 ¿Repasamos el lanzamiento a las 12?' }],
  typing: null,
  hint: false,
  tasks: [
    { id: 'contract', title: 'Revisar contrato', status: 'todo', group: 'overdue', urgent: true },
    { id: 'sprint', title: 'Presentación del sprint', status: 'in_progress', group: 'today' },
    { id: 'beta', title: 'Feedback de la beta', status: 'todo', group: 'today' },
  ],
  call: 0,
};

type Action =
  | { type: 'reset' }
  | { type: 'typing'; who: Person | null }
  | { type: 'say'; who: Person; text: string; mine?: boolean }
  | { type: 'hint'; on: boolean }
  | { type: 'toTask' }
  | { type: 'react'; emoji: string }
  | { type: 'done'; task: string }
  | { type: 'call'; people: number };

let nextId = 100;
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'reset':
      return initial;
    case 'typing':
      return { ...state, typing: action.who };
    case 'say':
      return {
        ...state,
        typing: null,
        // Only the latest few fit on screen.
        messages: [
          ...state.messages,
          { id: ++nextId, who: action.who, text: action.text, mine: action.mine },
        ].slice(-5),
      };
    case 'hint':
      return { ...state, hint: action.on };
    case 'toTask': {
      const messages = state.messages.map((m) =>
        m.who === 'marcos' && !m.task ? { ...m, task: true } : m,
      );
      return {
        ...state,
        hint: false,
        messages,
        tasks: [
          ...state.tasks.map((t) => ({ ...t, fresh: false })),
          { id: 'press', title: 'Nota de prensa', status: 'todo', group: 'week', fresh: true },
        ],
      };
    }
    case 'react':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.who === 'marcos' && m.task
            ? { ...m, reactions: [...(m.reactions ?? []), action.emoji] }
            : m,
        ),
      };
    case 'done':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.task ? { ...t, status: 'done', fresh: true } : { ...t, fresh: false },
        ),
      };
    case 'call':
      return { ...state, call: action.people };
  }
}

// [delay since the previous step (ms), action]
const script: [number, Action][] = [
  [900, { type: 'typing', who: 'lucia' }],
  [1500, { type: 'say', who: 'lucia', text: '¿Quién prepara la nota de prensa?' }],
  [900, { type: 'typing', who: 'marcos' }],
  [1500, { type: 'say', who: 'marcos', text: 'Yo me encargo, la tengo el jueves' }],
  [900, { type: 'hint', on: true }],
  [1500, { type: 'toTask' }],
  [1000, { type: 'react', emoji: '🙌' }],
  [700, { type: 'react', emoji: '🚀' }],
  [1300, { type: 'done', task: 'beta' }],
  [1300, { type: 'typing', who: 'irene' }],
  [1400, { type: 'say', who: 'irene', text: 'Empiezo la llamada de diseño, ¿os unís?' }],
  [600, { type: 'call', people: 1 }],
  [700, { type: 'call', people: 2 }],
  [700, { type: 'call', people: 3 }],
  [5000, { type: 'reset' }],
];

const tones = [
  'from-[oklch(0.75_0.14_255)] to-[oklch(0.55_0.2_268)]',
  'from-[oklch(0.85_0.13_75)] to-[oklch(0.68_0.17_45)]',
  'from-[oklch(0.8_0.12_165)] to-[oklch(0.6_0.14_200)]',
  'from-[oklch(0.78_0.14_330)] to-[oklch(0.58_0.2_300)]',
];

function Face({ who, className }: { who: Person; className?: string }) {
  const { t } = useTranslation();
  const person = people[who];
  return (
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-semibold text-white ring-2 ring-surface',
        tones[person.tone],
        className,
      )}
    >
      {t(person.name)[0]}
    </span>
  );
}

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function LiveDemo() {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initial);
  const [draft, setDraft] = useState('');
  // Once the visitor writes, the script stops so their messages stay.
  const [visitorTalking, setVisitorTalking] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = useState(false);

  useEffect(() => {
    const node = root.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setOnScreen(!!entry?.isIntersecting), {
      threshold: 0.3,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Play the script while visible; with reduced motion, jump straight to its last frame.
  useEffect(() => {
    if (visitorTalking) return;
    if (reducedMotion()) {
      for (const [, action] of script.slice(0, -1)) dispatch(action);
      return;
    }
    if (!onScreen) return;
    let step = 0;
    let timer: ReturnType<typeof setTimeout>;
    const run = () => {
      const entry = script[step % script.length];
      if (!entry) return;
      const [delay, action] = entry;
      timer = setTimeout(() => {
        dispatch(action);
        step += 1;
        run();
      }, delay);
    };
    run();
    return () => clearTimeout(timer);
  }, [onScreen, visitorTalking]);

  const send = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    if (!visitorTalking) {
      setVisitorTalking(true);
      dispatch({ type: 'typing', who: null });
      dispatch({ type: 'hint', on: false });
    }
    dispatch({ type: 'say', who: 'you', text, mine: true });
    setTimeout(() => dispatch({ type: 'typing', who: 'lucia' }), 600);
    setTimeout(
      () =>
        dispatch({
          type: 'say',
          who: 'lucia',
          text: '¡Así de fácil! Crea tu equipo gratis y pruébalo con los tuyos 🚀',
        }),
      2200,
    );
  };

  const groups: [Task['group'], string, string][] = [
    ['overdue', 'Vencidas', 'text-danger'],
    ['today', 'Hoy', ''],
    ['week', 'Esta semana', ''],
  ];

  return (
    <div
      ref={root}
      className="mx-auto mt-8 max-w-5xl rounded-[1.4rem] border border-white/12 bg-[oklch(0.13_0.025_268)] p-2 shadow-[0_40px_120px_-30px_oklch(0.6_0.18_262/0.55)]"
    >
      <div className="flex items-center gap-1.5 px-3 pt-1 pb-2.5">
        <span className="size-2.5 rounded-full bg-white/15" />
        <span className="size-2.5 rounded-full bg-white/15" />
        <span className="size-2.5 rounded-full bg-white/15" />
        <span className="ml-auto flex items-center gap-1.5 text-[11px] text-white/45">
          <span className="size-1.5 animate-pulse rounded-full bg-success" />
          {t('Demo en vivo · escribe tú también')}
        </span>
      </div>
      <div className="flex h-[26rem] gap-2 text-sm">
        {/* Sidebar */}
        <div aria-hidden className="hidden w-48 flex-col gap-0.5 p-2 sm:flex">
          <p className="mb-3 px-2 font-display font-semibold">{t('Estudio Alba')}</p>
          <p className="px-2 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
            {t('Canales')}
          </p>
          {['general', 'diseño', 'lanzamiento'].map((name, i) => (
            <p
              key={name}
              className={
                i === 2
                  ? 'flex items-center gap-2 rounded-lg bg-surface px-2 py-1.5 font-medium ring-1 ring-border'
                  : 'flex items-center gap-2 px-2 py-1.5 text-muted'
              }
            >
              <Hash className="size-3.5 text-subtle" />
              {t(name)}
            </p>
          ))}
          <p className="mt-4 px-2 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
            {t('Canales de voz')}
          </p>
          <p className="flex items-center gap-2 px-2 py-1.5 text-muted">
            <Phone className={cn('size-3.5', state.call ? 'text-success' : 'text-subtle')} />
            {t('Diseño')}
          </p>
          {state.call > 0 && (
            <div className="flex -space-x-1.5 pl-8">
              {(['irene', 'lucia', 'marcos'] as Person[]).slice(0, state.call).map((who) => (
                <Face key={who} who={who} className="animate-pop size-5 text-[9px]" />
              ))}
            </div>
          )}
        </div>

        {/* Conversation */}
        <div className="flex min-w-0 flex-1 flex-col rounded-xl border border-border bg-surface">
          <div className="flex items-center gap-2 border-b border-border px-4 py-3 font-semibold">
            <Hash className="size-4 text-subtle" /> {t('lanzamiento')}
            {state.call > 0 && (
              <span className="animate-pop ml-2 flex items-center gap-1.5 rounded-full bg-success-soft px-2 py-0.5 text-[11px] font-medium text-success">
                <span className="size-1.5 animate-pulse rounded-full bg-success" />
                {t('Llamada en curso · {{n}}', { n: state.call })}
              </span>
            )}
          </div>
          <div className="relative flex min-h-0 flex-1 flex-col justify-end gap-3.5 overflow-hidden p-4">
            {state.messages.map((message) => (
              <div key={message.id} className="animate-rise flex gap-3">
                <Face who={message.who} />
                <div className="min-w-0">
                  <p className="font-semibold">
                    {t(people[message.who].name)}
                    <span className="ml-2 text-[11px] font-normal text-subtle">{t('ahora')}</span>
                  </p>
                  <p className="break-words text-muted">
                    {message.mine ? message.text : t(message.text)}
                  </p>
                  {message.task && (
                    <span className="animate-rise mt-2 flex w-fit items-center gap-2 rounded-xl border border-border px-3 py-2">
                      <StatusIcon status="todo" />
                      <span className="text-[13px] font-medium">{t('Nota de prensa')}</span>
                      <span className="rounded-md bg-warning-soft px-1.5 text-[11px] text-warning">
                        {t('Jue')}
                      </span>
                    </span>
                  )}
                  {message.reactions && (
                    <span className="mt-1.5 flex gap-1">
                      {message.reactions.map((emoji) => (
                        <span
                          key={emoji}
                          className="animate-pop rounded-full border border-accent/40 bg-accent-soft px-2 py-0.5 text-[11px]"
                        >
                          {emoji} 1
                        </span>
                      ))}
                    </span>
                  )}
                </div>
                {state.hint && message.who === 'marcos' && !message.task && (
                  <span className="animate-pop absolute right-4 flex items-center gap-1.5 self-start rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs font-medium shadow-float">
                    <ListTodo className="size-3.5 text-accent" />
                    {t('Convertir en tarea')}
                    <MousePointer2 className="absolute -right-2 -bottom-3 size-4 fill-text text-surface" />
                  </span>
                )}
              </div>
            ))}
            <p className="h-4 text-xs text-subtle">
              {state.typing &&
                t('{{name}} está escribiendo…', { name: t(people[state.typing].name) })}
            </p>
          </div>
          <form onSubmit={send} className="flex items-center gap-2 border-t border-border p-2.5">
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              maxLength={140}
              aria-label={t('Escribe un mensaje en la demo')}
              placeholder={t('Escribe algo y pulsa Enter…')}
              className="h-9 min-w-0 flex-1 rounded-lg bg-surface-2 px-3 text-sm outline-none placeholder:text-subtle focus:ring-2 focus:ring-accent/40"
            />
            <button
              type="submit"
              aria-label={t('Enviar')}
              className="flex size-9 items-center justify-center rounded-lg bg-accent text-accent-fg transition hover:bg-accent-hover"
            >
              <SendHorizontal className="size-4" />
            </button>
          </form>
        </div>

        {/* My tasks */}
        <div
          aria-hidden
          className="hidden w-64 flex-col rounded-xl border border-border bg-surface p-3 lg:flex"
        >
          <p className="mb-3 font-semibold">{t('Mis tareas')}</p>
          {groups.map(([group, label, tone]) => {
            const tasks = state.tasks.filter((task) => task.group === group);
            if (tasks.length === 0) return null;
            return (
              <div key={group} className="mb-2">
                <p className={cn('mb-1 text-xs font-semibold', tone)}>{t(label)}</p>
                {tasks.map((task) => (
                  <p
                    key={task.id}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-1 py-1 transition-colors duration-700',
                      task.fresh && 'animate-rise bg-accent-soft',
                    )}
                  >
                    <span className={cn(task.fresh && task.status === 'done' && 'animate-pop')}>
                      <StatusIcon status={task.status} />
                    </span>
                    {task.urgent && <PriorityIcon priority="urgent" />}
                    <span className={task.status === 'done' ? 'text-subtle line-through' : ''}>
                      {t(task.title)}
                    </span>
                  </p>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
